import React, {
	useContext,
	useEffect,
	useLayoutEffect,
	useState,
	useRef,
	useCallback,
} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import { useHashAsState } from 'tbk-components/src/hooks';
import ElasticMapper from 'tbk-components/src/services/ElasticMapper';
import qs from 'qs';
import { IImage } from 'tbk-components/src/components/Image';
import Loading from 'tbk-components/src/components/Loading';
import Dropdown from 'bootstrap/js/dist/dropdown';
import Icon from 'tbk-components/src/components/Icon';
import Breadcrumb from 'tbk-components/src/components/Breadcrumb';
import { ILink } from 'tbk-components/src/components/Button';
import { filter, set, split } from 'lodash';
import BlogRollPagination from './BlogRollPagination';
import { IResourceCardProps } from '../ResourceCard';
import ResourcesFilterBar from '../ResourcesFilterBar';
import ResourceCardListing, {
	IResourceCardsi18n,
} from '../ResourceCardListing';
import ResourceCta, { IResourceCtaProps } from '../ResourceCta';
import AdvancedMedia from '../AdvancedMedia';
import SmallProductGrid, { ISmallProductsI18n } from './SmallProductGrid';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import middleware from './search-middleware';
import ResourceTableListing, {
	IResourceTablesi18n,
} from '../ResourceTableListing';

/**
 * @usePostData
 * @keepallproperties
 * @post_type any
 */

// This interface is used for Calian resources and will loop over various card listings

export interface IResourceMultiCardListingProps extends IResourceCardProps {
	resourceCardId?: string; // This is used to set the ID of the card listing div for filtering
}

export interface IResourceIndexFilterI18n {
	view?: string;
	hide?: string;
	resetFilters?: string;
	filterSearch?: string;
	search?: string;
	submitSearch?: string;
	resultsFor?: string;
	results?: string;
	go?: string;
	toggleFilters?: string;
}

export interface ISideProductProps extends IBaseElementProps {
	header?: string;
	image?: IImage;
	url?: ILink;
}
export interface IResourceIndexProps extends IBaseElementProps {
	heading?: string;
	/**
	 * @control richtext
	 */
	excerpt?: string;
	/**
	 * @noUI
	 */
	context?: any;
	/**
	 * @noUI
	 */
	assets?: any;
	/**
	 * Blog tiles
	 */
	cards?: IResourceCardProps[];
	/**
	 * For multiple carousel listings
	 */
	multiCardListing?: IResourceMultiCardListingProps[];
	/**
	 * In order to sync into ResourceCardListing we can offer a `carousel` layout.
	 * While this works, it should not be the preferred design and UX option. Grid or List is preferred.
	 */
	listingStyle?: 'grid' | 'list' | 'carousel';
	showOnlyLatestPosts?: boolean;
	numberOfPostsToShow?: number;
	filterStyle?: 'vertical' | 'horizontal';
	/**
	 * This option will use a table listing component instead of the card listing component.
	 */
	gnssCardListing?: boolean;
	/**
	 * This is the CTA that appears in the filter bar. It is not the same as the CTA that appears in the results.
	 *
	 */
	filterBarCta?: IResourceCtaProps;
	staticSideProductListing?: ISideProductProps[];
	showPagination?: boolean;
	resultsCta?: IResourceCtaProps;
	showCardTypeLabels?: boolean;
	/**
	 * @control richtext
	 */
	footerLabel?: string;
	/**
	 * @noUI
	 */
	adBanner?: any; // Expected to be a React component
	/**
	 * @noUI
	 */
	posts?: {
		total: number;
		hits: any[];
	};
	/**
	 * @noUI
	 */
	fetchArgs?: any;
	isProductsOnlySearch?: boolean;
	filterBarTargetUrl?: string;
	filterSearchPlaceholder?: string;
	filterLabel?: string;
	displaySearchTaxonomies?: boolean;
	noResultsMessage?: string;
	threeCharacterMinimum?: string;
	singleResultLabel?: string;
	multipleResultsLabel?: string;
	defaultResultMessage?: string;
	enableStartScreen?: boolean;
	searchListing?: boolean;
	sideProductsI18n?: ISmallProductsI18n;
	resourceListingBackgroundColor?: 'light' | 'transparent' | 'white';
	/**
	 *These themes only apply if the filter style is horizontal
	 */
	filterHeaderBackgroundStyle?: 'grey' | 'themed' | 'light';
	/**
	 * @noUI
	 */
	breadcrumbsLinks?: ILink[];
	/**
	 * This removes the total results count above the cards
	 * @noUI
	 */
	disableResultsCount?: boolean;
	/**
	 * This removes the different view options the user can select from
	 * @noUI
	 */
	disableUserGridOptions?: boolean;
	/**
	 * This will turn off the results label right under the search bar
	 * @noUI
	 */
	showSearchResultsCount?: boolean;
	showFilterBar?: boolean;
	/**
	 * This will be populated on archive pages, usually about the post type.
	 * @noUI
	 */
	filterHeaderEyebrow?: ILink;
	/**
	 * @noUI
	 */
	children?: any;
	filterBarI18n?: IResourceIndexFilterI18n;
	enableSideProductListing?: boolean;
	/*
	 * @noUI
	 */
	onFilterCardsTagClick?: (event: any) => any;
	/*
	 * @noUI
	 */
	enableProductCompare?: boolean;
	productCompareLink?: ILink;
	resourceCardsi18n?: IResourceCardsi18n;
	tablei18n?: IResourceTablesi18n;
}

const ResourceIndex: React.FC<IResourceIndexProps> = withAppContext(
	({
		heading,
		context,
		cards = [],
		listingStyle = 'grid',
		showOnlyLatestPosts = false,
		numberOfPostsToShow,
		showPagination = true,
		posts,
		isProductsOnlySearch = false,
		excerpt,
		fetchArgs,
		filterBarCta,
		filterSearchPlaceholder,
		enableSideProductListing = false,
		filterLabel,
		filterBarI18n,
		staticSideProductListing = [],
		displaySearchTaxonomies = true,
		searchListing = false,
		filterBarTargetUrl,
		noResultsMessage,
		threeCharacterMinimum,
		defaultResultMessage,
		enableStartScreen = false,
		breadcrumbsLinks = [],
		footerLabel,
		resultsCta,
		disableResultsCount = false,
		disableUserGridOptions = false,
		showSearchResultsCount = false,
		filterHeaderBackgroundStyle = 'light',
		filterStyle = 'vertical',
		multiCardListing = [],
		resourceListingBackgroundColor = 'transparent',
		adBanner,
		showFilterBar = true,
		showCardTypeLabels = false,
		enableProductCompare = false,
		filterHeaderEyebrow,
		children,
		id,
		className,
		classNames = [],
		singleResultLabel,
		sideProductsI18n,
		multipleResultsLabel,
		resourceCardsi18n,
		tablei18n,
		gnssCardListing = false,
		productCompareLink,
	}) => {
		// @ts-ignore
		const { assets: contextAssets, Api } = useContext(context);
		const [assets, setAssets] = useState<any>(null);
		const [dynamicAssets, setDynamicAssets] = useState<any>(null);
		const [resourceIndex, setResourceIndex] = useState<any>(null);
		const [termMap, setTermMap] = useState<any>(null);
		const [total, setTotal] = useState<number | null>(null);
		const [loading, setLoading] = useState<boolean>(false);
		const [startScreen, setStartScreen] = useState<boolean>(false);
		const [commonTermAssets, setCommonTermAssets] = useState<any>();
		const [multiCardListings, setMultiCardListings] = useState<any>(null);
		const [sideProductListing, setSideProductListing] = useState<
			ISideProductProps[]
		>(staticSideProductListing);
		const [cardViewStyle, setCardViewStyle] = useState<
			'grid' | 'list' | 'carousel'
		>(searchListing ? 'list' : listingStyle);
		const { t } = useTranslation();
		//Set the Dynamic Breadcrumbs and pull from WP Admin
		const [breadcrumbs, setBreadcrumbs] =
			useState<ILink[]>(breadcrumbsLinks);
		const [displayView, setDisplayView] = useState<any>('carousel');
		const generatedSingleResultLabel = singleResultLabel || t('result');
		const generatedMultipleResultsLabel =
			multipleResultsLabel || t('results');

		useEffect(() => {
			if (document?.body.classList.contains('wp-admin')) {
				setBreadcrumbs([
					{
						label: 'Loading breadcrumbs from server...',
						href: '#',
					},
				]);
				const titleEl = document.querySelector('.editor-post-title');
				const updateLastBreadcrumb = () => {
					setBreadcrumbs((breadcrumbs) => {
						const last = breadcrumbs.pop();
						return [
							...breadcrumbs,
							{
								...last,
								label: titleEl.textContent,
							},
						];
					});
				};

				Api.prepare().then(() => {
					// @ts-ignore
					Api.getBreadcrumbs(document.getElementById('post_ID').value)
						.then(setBreadcrumbs)
						.then(() => {
							updateLastBreadcrumb(); // in case it's changed in the editor
						});
				});

				// Now we're going to set up an observer on the title element so that we can update the breadcrumbs
				// when the title changes.
				const config = { characterData: true, subtree: true };

				// Callback function to execute when mutations are observed
				const callback = (mutationList, observer) => {
					for (const mutation of mutationList) {
						if (mutation.type === 'characterData') {
							updateLastBreadcrumb();
						}
					}
				};

				// Create an observer instance linked to the callback function
				const observer = new MutationObserver(callback);

				// Start observing the target node for configured mutations
				observer.observe(titleEl, config);

				// Remove the listener on dismount.
				return () => {
					observer.disconnect();
				};
			}
		}, []);

		const taxonomy_parameters = ((taxonomies) => {
			const t: any = {};
			(taxonomies || []).forEach((tax: any) => {
				t[tax.taxonomy] = 'csv';
			});
			return t;
		})(assets?.taxonomies);
		const [state, hash, updateState, normalizeState] = useHashAsState({
			parameters: {
				page: 'int',
				...taxonomy_parameters,
				meta_query: [],
				search: 'string',
				total: 'int',
			},
			defaults: {
				search: '',
				page: 1,
				total,
			},
		});

		// This block of code converts URL variables and injects them into the hash functionality.
		// This is used so that when the user clicks a link with a URL variable, the state is updated.
		// We keep this functionality as generic as possible, so that menus are not dependent on the page they are on.
		useEffect(() => {
			const params = new window.URLSearchParams(window.location.search);
			const urlState = {
				...state,
			};

			params.forEach((value, key) => {
				urlState[key] = value;
			});

			if (!window.location.hash) {
				const newUrl =
					window.location.pathname + '#!' + qs.stringify(urlState);
				window.history.replaceState(null, '', newUrl);
			}
		}, []);

		// ref to pass down to pagination so it can force a scroll to top of ResourceIndex when user changes page.
		const topRef = useRef(null);

		useEffect(() => {
			if (assets) {
				updateState({ total });
			}
		}, [total, assets]);

		let resetScreen = false;
		const fetchPostsFromApi = useCallback(
			debounce((args: any) => {
				setTotal(null);
				if (!isProductsOnlySearch) {
					let apiCall;
					// As a proof of concept, we are only going to use this on the resources sub site. As we implement more functionality, we can expand this to other areas.
					if(args.resource_section == 1 && args.post_type.length === 0 && args.search === '' && (!args.business_unit || args.business_unit === '')) {
						apiCall = Api.getResourcesDirect(args);
					} else {
						apiCall = Api.getBlogRollSearchWP(args);
					}
					apiCall.then((results: any) => {
							resetScreen = true;
							//Default Single Search
							if (results?.hits) {
								setDisplayView('search');
								setResourceIndex(ElasticMapper(results));
								setTotal(results?.total); // This returns an object with the total already provided
								if (results?.sideProductListing) {
									setSideProductListing(
										results.sideProductListing,
									);
								}
							} else {
								setDisplayView('carousel');
								setMultiCardListings(results);
								setTotal(results?.length); // This is just a list of cards, so we need to calculate the total ourselves.
							}
							if (results?.commonTerms)
								setCommonTermAssets(results.commonTerms);
						})
						.finally(() => {
							if (resetScreen) {
								setLoading(false);
								setStartScreen(false);
							}
						});
					if (enableSideProductListing) {
						Api.getSiteSearchWP({
							posts_per_page: 8,
							post_type: [
								'gnss_product',
								'cable_access',
								'space_satcom',
								'space_antenna',
							],
							isSimpleSearch: true,
							allSearchList: false,
							search: args.search,
						})
							.then((results: any) => {
								if (results.products)
									setSideProductListing(results.products);
							})
							.finally(() => {
								if (resetScreen) {
									setLoading(false);
									setStartScreen(false);
								}
							});
					}
				} else {
					const massagedProductArgs = middleware(args);
					Api.getSiteSearchWP(massagedProductArgs)
						.then((results: any) => {
							resetScreen = true;
							if (results.hits) {
								setDisplayView('search');
								setResourceIndex(ElasticMapper(results));
								setTotal(results?.total); // This returns an object with the total already provided
							}

							if (results.counts) {
								const newAssets = {
									taxonomies: [],
									meta_query: [],
								};

								for (const [key, value] of Object.entries(
									results.counts,
								)) {
									const splitKey = key.split('__');
									const maybeTaxonomy = splitKey[0];

									if (maybeTaxonomy === 'taxonomy') {
										//The last part of the split key is the term label.
										const massagedTermLabel = splitKey[
											splitKey.length - 1
										]
											.replace('&amp;', '&')
											.replace('&gt;', '>');

										const obj = {
											taxonomy: splitKey[1],
											terms: [
												{
													name: massagedTermLabel,
													count: value,
												},
											],
										};

										//Check if the taxonomy already exists
										const existingTaxonomy =
											newAssets.taxonomies.find(
												(taxonomy: any) =>
													taxonomy.taxonomy ===
													splitKey[1],
											);
										if (existingTaxonomy) {
											//If it does, add the term to the terms array

											existingTaxonomy.terms.push({
												name: massagedTermLabel,
												count: value,
											});
										} else {
											//If it does not, add the object to the taxonomies array
											newAssets.taxonomies.push(obj);
										}
									}
								}
								setDynamicAssets(newAssets);
							}
						})
						.finally(() => {
							if (resetScreen) {
								setLoading(false);
								setStartScreen(false);
							}
						});
				}
			}, 500),
			[],
		);

		// Here's the effect that actually gets the posts from the server whenever the search has changed.
		const { total: ignored, ...relevantArgs } = state;
		const relevantHash = qs.stringify(relevantArgs);

		useEffect(() => {
			if (posts) {
				setTotal(posts.total);
				setResourceIndex(ElasticMapper(posts));
				return;
			}
			if (!assets) {
				return;
			}

			let args = qs.parse(relevantHash);

			args.posts_per_page = assets?.posts_per_page;
			if (fetchArgs?.posts_per_page) {
				args.posts_per_page = fetchArgs.posts_per_page;
			}
			if (fetchArgs) {
				args = {
					...fetchArgs,
					...args,
				};
			}

			// override whatever is in args to only show posts
			if (numberOfPostsToShow) {
				args.posts_per_page = `${numberOfPostsToShow}`; // cast to string
			}
			fetchPostsFromApi(args);
			if (resourceIndex) setResourceIndex(null);
			if (multiCardListing) setMultiCardListings(null);
			setCommonTermAssets(null);
			setLoading(true);

			if (numberOfPostsToShow) {
				args.posts_per_page = `${numberOfPostsToShow}`; // cast to string
			}
		}, [
			relevantHash,
			assets,
			fetchArgs,
			posts,
			Api,
			showOnlyLatestPosts,
			numberOfPostsToShow,
		]);

		useEffect(() => {
			if (!assets) return;
			if (assets?.taxonomies) {
				assets.taxonomies.forEach((taxonomy: any) => {
					//Reset the term count to 0
					if (taxonomy.terms?.length > 0) {
						taxonomy.terms.forEach((term: any) => {
							term.count = 0;
						});
					}

					const dynamicTaxonomy = dynamicAssets.taxonomies.find(
						(dynamicTaxonomy: any) =>
							dynamicTaxonomy.taxonomy === taxonomy.taxonomy,
					);

					if (dynamicTaxonomy) {
						taxonomy.terms.forEach((term: any) => {
							const dynamicTerm = dynamicTaxonomy.terms.find(
								(dynamicTerm: any) =>
									dynamicTerm.name === term.name,
							);

							if (dynamicTerm) {
								term.count = dynamicTerm.count;
							}
						});
					}
				});
			}
		}, [dynamicAssets]);

		useEffect(() => {
			const assetReqArgs = { ...fetchArgs };
			const setAssetsAndTermMap = (results: any) => {
				setAssets(results);
				const map: any = {};
				if (results.taxonomies) {
					results.taxonomies.forEach((taxonomy: any) => {
						taxonomy?.terms &&
							taxonomy.terms.forEach((term: any) => {
								map[term.term_id] = term;
							});
					});
				}
				setTermMap(map);
			};
			if (contextAssets) {
				setAssetsAndTermMap(contextAssets);
			} else {
				Api.getResourceSearchAssets(assetReqArgs).then(
					(results: any) => {
						setAssetsAndTermMap(results);
					},
				);
			}

			if (!topRef.current) {
				return;
			}

			const initDropdown = () => {
				const dropdownTriggerList = [].slice.call(
					topRef.current.querySelectorAll('.dropdown'),
				);

				dropdownTriggerList.map(
					(dropdownTriggerEl) => new Dropdown(dropdownTriggerEl),
				);
			};

			initDropdown();
		}, [contextAssets, Api, fetchArgs]);
		const loaderElement = (
			<div className="loading-wrapper">
				<Loading />
			</div>
		);

		const filterTagChange = (event: any) => {
			event.preventDefault();
			const me = event.target;
			const taxonomy = me.getAttribute('data-taxonomy');
			const term = me.getAttribute('data-term');
			let newArgs = { ...relevantArgs };
			newArgs.page = 1;
			const existingTaxonomy = newArgs[taxonomy];

			topRef.current.scrollIntoView({ behavior: 'smooth' });

			let clearedState: any = {
				search: newArgs.search || '',
				page: 1,
			};
			// Inside newArgs, find all the objects that aren't 'search', 'page', or 'posts_per_page' and set them to an empty array. and set them inside clearedState
			//These have to be here for some reason...
			//This is not ideal, but if we do not include it in the clearedState, the filters seem to ignore the reset.
			//This will find object keys like "post_type", "industries", etc.
			//We essentially want it to print out something like this (besides search and page):
			// post_type: [],
			// solutions: [],
			// industry: [],
			// division: [],

			const keysToExclude = ['search', 'page', 'posts_per_page'];
			for (const key in newArgs) {
				if (!keysToExclude.includes(key)) {
					clearedState[key] = [];
				}
			}

			if (!existingTaxonomy) {
				newArgs = clearedState;
				newArgs[taxonomy] = [term];

				updateState(newArgs);
			} else {
				clearedState[taxonomy] = [term];
				updateState(clearedState);
			}
		};

		if (!assets) {
			return loaderElement;
		}
		const resourceFilterBarRender = (
			<ResourcesFilterBar
				isProductsOnlySearch={isProductsOnlySearch}
				searchPlaceholder={filterSearchPlaceholder}
				searchTaxonomies={assets}
				filterLabel={filterLabel}
				filters={state}
				assets={assets}
				showFilterBar={showFilterBar}
				onChange={updateState}
				listStyle={filterStyle}
				commonTerms={commonTermAssets}
				total={total}
				resultsLoaded={loading}
				filterHeaderEyebrow={filterHeaderEyebrow}
				value={state.search}
				i18n={filterBarI18n}
				filterHeaderBackgroundStyle={filterHeaderBackgroundStyle}
				showSearchResultsCount={
					showSearchResultsCount && !loading && !!state.search
				}
				filterHeader={filterStyle === 'horizontal' ? heading : ''}
				excerpt={filterStyle === 'horizontal' ? excerpt : ''}
				inProductIndex={true}
			/>
		);

		return (
			<div
				className={
					className ||
					cn(
						'resource-index',
						`resource-index--filter-style-${filterStyle}`,
						...classNames,
					)
				}
				ref={topRef}
				id={id}
			>
				{filterStyle === 'horizontal' && resourceFilterBarRender}
				<div
					className={cn(
						'resource-index__content-wrapper pb-md-6 pb-7',
						`bg-${resourceListingBackgroundColor}`,
						filterStyle === 'vertical' && 'px-lg-3',
						resourceListingBackgroundColor === 'light' &&
							'pt-3 pt-md-7',
					)}
				>
					<div
						className={cn(
							'resource-index__content d-flex gap-md-5 gap-3 flex-column mw-1240px mx-auto',
							filterStyle !== 'vertical' && 'gap-lg-8',
							'flex-lg-row',
							(sideProductListing?.length > 0 ||
								cards?.length > 0) &&
								'mt-lg-5',

							!sideProductListing?.length &&
								'resource-index__content--no-products',
						)}
					>
						{(sideProductListing?.length > 0 ||
							(filterStyle === 'vertical' &&
								resourceFilterBarRender) ||
							filterBarCta) && (
							<div className="resource-index__filter-wrapper w-100 flex-grow-1 flex-shrink-0">
								{filterStyle === 'vertical' &&
									resourceFilterBarRender}
								{filterBarCta && (
									<ResourceCta
										classNames={[
											'mt-4 px-3 px-lg-0 mt-md-6',
										]}
										{...filterBarCta}
										sizing={
											filterStyle == 'vertical'
												? 'small'
												: 'large'
										}
									/>
								)}
								{sideProductListing?.length > 0 && (
									<SmallProductGrid
										title={t('Calian Products')}
										classNames={['mt-md-4 py-4 px-3']}
										products={sideProductListing}
										i18n={sideProductsI18n}
									/>
								)}
							</div>
						)}
						<div
							className={cn(
								'resource-index__content-wrapper w-100 flex-grow-1 flex-shrink-1',
								(sideProductListing?.length > 0 ||
									cards?.length > 0) &&
									'px-3',
							)}
						>
							<div className="resource-index__results-header p-3 pt-0 mw-850px w-100">
								{breadcrumbs?.length > 0 && (
									<Breadcrumb
										classNames={['my-3']}
										links={breadcrumbs}
									/>
								)}
								{filterStyle === 'vertical' && (
									<>
										{heading && <h1>{heading}</h1>}
										{excerpt && (
											<p
												className="mb-0"
												dangerouslySetInnerHTML={{
													__html: excerpt,
												}}
											/>
										)}
									</>
								)}
							</div>
							<div className="resource-index__results-container">
								{children && displayView !== 'carousel' && (
									<div className="ad-banner mb-2 mb-md-4">
										{children}
									</div>
								)}
								{(!disableResultsCount ||
									!disableUserGridOptions) && (
									<div
										className={cn(
											'resource-index__results-top-nav mb-2 px-3 pe-0 mx-auto d-flex align-items-center justify-content-between',
											!sideProductListing?.length &&
												filterStyle === 'vertical' &&
												'px-md-0',
										)}
									>
										{resourceIndex && !!total && (
											<p className={cn('mb-0')}>
												<b>{total}</b>{' '}
												{total === 1
													? generatedSingleResultLabel
													: generatedMultipleResultsLabel}
											</p>
										)}
										{!disableUserGridOptions && (
											<div className="resource-index__card-listing-options align-self-end justify-self-end ms-auto me-0 d-none d-md-flex rounded-4 border border-1 border-light bg-light overflow-hidden">
												<button
													className={cn(
														'text-decoration-none py-2 d-block px-3 outline-0 border-0',
														cardViewStyle === 'grid'
															? 'bg-white text-charcoal'
															: 'bg-transparent text-mid-grey',
													)}
													onClick={(e) => {
														e.preventDefault();
														setCardViewStyle(
															'grid',
														);
													}}
													aria-label="Grid View"
												>
													<Icon name="squares-four" />
												</button>
												<button
													className={cn(
														'text-decoration-none py-2 d-block px-3 outline-0 border-0',
														cardViewStyle === 'list'
															? 'bg-white text-charcoal'
															: 'bg-transparent text-mid-grey',
													)}
													onClick={(e) => {
														e.preventDefault();
														setCardViewStyle(
															'list',
														);
													}}
													aria-label="List View"
												>
													<Icon name="rows" />
												</button>
											</div>
										)}
									</div>
								)}
								{loading ? (
									<div className="resource-index__results-loading my-4">
										<Loading />
									</div>
								) : (
									<>
										{total ? (
											<>
												{resourceIndex && (
													<div className="resource-index__cards-container">
														{!gnssCardListing ? (
															<ResourceCardListing
																resources={
																	resourceIndex
																}
																allWide={
																	searchListing ||
																	cardViewStyle ===
																		'list'
																}
																showTypeLabels={
																	showCardTypeLabels
																}
																onFilterTagClick={
																	filterTagChange
																}
																allSearchCards={
																	searchListing
																}
																enableProductCompare={
																	enableProductCompare
																}
																productCompareLink={
																	productCompareLink
																}
																className="resource-index__resource-card-listing"
																listingStyle={
																	cardViewStyle
																}
																resourceCardsi18n={
																	resourceCardsi18n
																}
															/>
														) : (
															<ResourceTableListing
																resources={
																	resourceIndex
																}
																enableProductCompare={
																	enableProductCompare
																}
																productCompareLink={
																	productCompareLink
																}
																resourceCardsi18n={
																	resourceCardsi18n
																}
																tablei18n={
																	tablei18n
																}
															></ResourceTableListing>
														)}
													</div>
												)}
												{showPagination &&
													(sideProductListing?.length >
														0 ||
														cards.length > 0 ||
														resourceIndex) && (
														<>
															<BlogRollPagination
																page={
																	state.page ||
																	1
																}
																perPage={
																	fetchArgs?.posts_per_page ||
																	assets?.posts_per_page
																}
																total={total}
																onChange={(
																	page: any,
																) =>
																	updateState(
																		{
																			page,
																		},
																	)
																}
																topRef={topRef}
															/>
														</>
													)}
												{multiCardListings?.length >
													0 && (
													<div className="resource-index__multi-card-listing-wrapper">
														{multiCardListings.map(
															(
																cardListing,
																index,
															) => (
																<div
																	key={index}
																	data-listing-term-id={
																		cardListing.resourceCardId
																	}
																	className="resource-index__multi-card-listing"
																>
																	<ResourceCardListing
																		{...cardListing}
																		resourceCardsi18n={
																			resourceCardsi18n
																		}
																	/>
																</div>
															),
														)}
													</div>
												)}
											</>
										) : total === null ? (
											<div className="resource-index__results-loading my-4">
												<Loading />
											</div>
										) : (
											<div className="resource-index__results-none px-5">
												<h3 className="my-5 mw-250px mx-auto mw-md-none mb-10 mb-xl-12 h6 text-center">
													{displayView === 'search' && state.search.length < 3
														? threeCharacterMinimum ||
														  t(
																'Please enter at least 3 characters',
														  )
														: noResultsMessage ||
														  t(
																'There are currently no results available for the selected criteria',
														  )}
												</h3>
											</div>
										)}
									</>
								)}
								{footerLabel && (
									<p
										className="mx-auto d-block mb-0 mt-7 mt-md-9 mw-850px"
										dangerouslySetInnerHTML={{
											__html: footerLabel,
										}}
									/>
								)}
							</div>
							{children && displayView == 'carousel' && (
								<div className="ad-banner mt-2 mt-md-6">
									{children}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	},
);
export default ResourceIndex;
